import { StatusCodes } from "http-status-codes"
import { v4 as uuid } from "uuid"
import { GeocodingResponse } from "../types/Location"
import { Provider, ProviderCount, TypeaheadProvider } from "../types/Providers"
import { HasCarrotPartnerResponse } from "../types/HasCarrotPartnerResponse"
import ResponseError from "../types/ResponseError"
import { reportError } from "../utils/ProviderFinderErrorReporting"
import Settings from "./Settings"
import { HasCustomNetworksResponse } from "../types/HasCustomNetworksResponse"
import {
  GetCarrotMatchMatchesRequest,
  GetCarrotMatchMatchesResponse
} from "#/components/carrot-match-fertility-clinic-precision-matching/utils/carrotMatchTypes"
import { AirtableDoula, TypeaheadDoula } from "#/pages/provider-finder/recommend-doula/RecommendDoulaForm"
import { CountryCode } from "#/utils/CountryCode"
import { GooglePlacesV1Response } from "#/pages/provider-finder/hooks/usePlacesAutocomplete"

class ProviderFinderClientError extends Error {
  readonly correlationId?: string

  constructor(message: string, { cause, correlationId }: { cause?: Error; correlationId?: string }) {
    super(message)
    this.name = this.constructor.name
    this.cause = cause
    this.correlationId = correlationId

    Object.setPrototypeOf(this, ProviderFinderClientError.prototype)
  }
}

export class ProviderFinderClient {
  baseUrl: string
  handleSessionExpiration: () => void

  constructor(baseUrl: string, handleSessionExpiration: () => void) {
    this.baseUrl = baseUrl
    this.handleSessionExpiration = handleSessionExpiration
  }

  async processJsonResponse<ResponseType>(response: Response, correlationId: string): Promise<ResponseType> {
    if (response.status === StatusCodes.OK) {
      return response?.json()
    }
    throw new ResponseError(response, correlationId)
  }

  createJsonHeaders(correlationId: string): Headers {
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("X-React-App-Version", __APP_VERSION__ || "local")
    headers.append("Content-Type", "application/json")
    headers.append("X-Authorizer-Domain", Settings.AUTHORIZER_DOMAIN)
    return headers
  }

  async get<ResponseType>(url: RequestInfo | URL): Promise<ResponseType> {
    const correlationId = uuid()

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: this.createJsonHeaders(correlationId),
        credentials: "include"
      })

      return await this.processJsonResponse(response, correlationId)
    } catch (error) {
      error.correlationId = correlationId

      if (error instanceof ResponseError && error.statusCode === StatusCodes.UNAUTHORIZED) {
        this.handleSessionExpiration()
        return null
      }

      reportError(
        new ProviderFinderClientError(`Failed to fetch ${url} `, {
          cause: error, // TODO Sentry groups this based on a custom grouping rule (src/utils/ErrorReporting.ts) with a wide net of anything with "Failed to fetch" in the stack trace.
          correlationId
        })
      )

      throw error
    }
  }

  async post<ResponseType>(url: RequestInfo | URL, body: unknown): Promise<ResponseType> {
    const correlationId = uuid()

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.createJsonHeaders(correlationId),
        credentials: "include",
        body: JSON.stringify(body)
      })

      return await this.processJsonResponse(response, correlationId)
    } catch (error) {
      error.correlationId = correlationId

      if (error instanceof ResponseError && error.statusCode === StatusCodes.UNAUTHORIZED) {
        this.handleSessionExpiration()
        return null
      }

      reportError(
        new ProviderFinderClientError(`Failed to fetch ${url} `, {
          cause: error,
          correlationId
        })
      )

      throw error
    }
  }

  async getLocationDetailsForAddress(locationString: string): Promise<GeocodingResponse> {
    const url = new URL(`${this.baseUrl}/geocoding`)
    url.searchParams.set("locationString", locationString)

    return this.get(url)
  }

  async getProviders(params: URLSearchParams): Promise<Provider[]> {
    const url = new URL(`${this.baseUrl}/providers`)
    url.search = new URLSearchParams(params).toString()

    return this.get(url)
  }

  async getProvidersByProviderUuids(providerUuidsString: string, params: URLSearchParams): Promise<Provider[]> {
    const url = new URL(`${this.baseUrl}/providers/${providerUuidsString}`)
    url.search = params.toString()

    return this.get(url)
  }

  async getHasProviderTypeInCountry(companyCountryCode: string): Promise<ProviderCount> {
    const url = new URL(`${this.baseUrl}/has-provider-type-in-country`)
    url.searchParams.set("companyCountryCode", companyCountryCode)

    return this.get(url)
  }

  async getTypeaheadProviders(
    providerNameSubstring: string,
    companyCountryCode: string,
    providerType: string
  ): Promise<TypeaheadProvider[]> {
    const url = new URL(`${this.baseUrl}/providers-member`)
    url.searchParams.set("companyCountryCode", companyCountryCode)
    url.searchParams.set("providerNameSubstring", providerNameSubstring)
    url.searchParams.set("providerType", providerType)

    return this.get(url)
  }

  async getTypeaheadDoulas(doulaNameSubstring: string, countryCode: CountryCode): Promise<TypeaheadDoula[]> {
    const url = new URL(`${this.baseUrl}/recommend-doula-search`)
    url.searchParams.set("providerNameSubstring", doulaNameSubstring)
    url.searchParams.set("companyCountryCode", countryCode)

    return this.get(url)
  }

  async getHasCarrotPartner(companyCountryCode: string): Promise<HasCarrotPartnerResponse> {
    const url = new URL(`${this.baseUrl}/carrot-partner`)
    url.searchParams.set("companyCountryCode", companyCountryCode)

    return this.get(url)
  }

  async getHasCustomNetworks(): Promise<HasCustomNetworksResponse> {
    const url = new URL(`${this.baseUrl}/has-custom-network`)
    return this.get(url)
  }

  async getCarrotMatchMatches(payload: GetCarrotMatchMatchesRequest): Promise<GetCarrotMatchMatchesResponse[]> {
    const url = new URL(`${this.baseUrl}/carrot-match-matches`)
    return this.post(url, payload)
  }

  async writeDoulaToAirtable(payload: AirtableDoula): Promise<void> {
    const url = new URL(`${this.baseUrl}/write-doula-to-airtable`)
    url.searchParams.set("companyCountryCode", payload.countryCode)
    url.searchParams.set("coreOrPro", payload.coreOrPro)
    if (payload.state) {
      url.searchParams.set("state", payload.state)
    }
    return this.post(url, { primaryEmail: payload.primaryEmail, name: payload.name })
  }

  async getPlacePredictions(input: string, language: string): Promise<GooglePlacesV1Response> {
    const url = new URL(`${this.baseUrl}/places-autocomplete`)
    url.searchParams.set("input", input)
    url.searchParams.set("languageCode", language)
    return this.get(url)
  }
}
